import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import { Title, useNotify } from 'react-admin';
import { httpClient } from '../../dataProvider';

const apiUrl = process.env.REACT_APP_API_URL;

type CacheWarmStatus = {
  service?: string;
  isRunning?: boolean;
  status?: Record<string, string>;
};

export const CacheControlList = () => {
  const notify = useNotify();
  const [isLoadingStatus, setIsLoadingStatus] = React.useState<boolean>(true);
  const [isTriggering, setIsTriggering] = React.useState<boolean>(false);
  const [status, setStatus] = React.useState<CacheWarmStatus | null>(null);

  const fetchStatus = React.useCallback(async () => {
    if (!apiUrl) {
      notify('REACT_APP_API_URL is missing', { type: 'warning' });
      setIsLoadingStatus(false);
      return;
    }

    setIsLoadingStatus(true);
    try {
      const { json } = await httpClient(`${apiUrl}/cache/warm/status`);
      setStatus(json);
    } catch (error: any) {
      notify(`Kunde inte hämta status: ${error?.message || 'okänt fel'}`, {
        type: 'error',
      });
    } finally {
      setIsLoadingStatus(false);
    }
  }, [notify]);

  const handleTriggerWarmCache = async () => {
    if (!apiUrl) {
      notify('REACT_APP_API_URL is missing', { type: 'warning' });
      return;
    }

    setIsTriggering(true);
    try {
      await httpClient(`${apiUrl}/cache/warm`, { method: 'POST' });
      notify('Cache-rensning och uppvärmning triggad', { type: 'info' });
      await fetchStatus();
    } catch (error: any) {
      notify(`Kunde inte trigga cache-warm: ${error?.message || 'okänt fel'}`, {
        type: 'error',
      });
    } finally {
      setIsTriggering(false);
    }
  };

  React.useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const activeState = status?.status?.ActiveState || '-';
  const subState = status?.status?.SubState || '-';
  const result = status?.status?.Result || '-';
  const lastStart = status?.status?.ExecMainStartTimestamp || '-';
  const lastEnd = status?.status?.ExecMainExitTimestamp || '-';

  return (
    <Card>
      <Title title="Cache Control" />
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h5">Cache Control</Typography>
          <Typography variant="body2">
            Trigga rensning och uppvärmning av API-cache via systemd-service.
          </Typography>

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={handleTriggerWarmCache}
              disabled={isTriggering}
            >
              {isTriggering ? 'Kör...' : 'Rensa och värm cache'}
            </Button>
            <Button
              variant="outlined"
              onClick={fetchStatus}
              disabled={isLoadingStatus || isTriggering}
            >
              Uppdatera status
            </Button>
          </Stack>

          {isLoadingStatus ? (
            <CircularProgress size={24} />
          ) : (
            <Stack spacing={1}>
              <Typography variant="body2">Service: {status?.service || '-'}</Typography>
              <Typography variant="body2">ActiveState: {activeState}</Typography>
              <Typography variant="body2">SubState: {subState}</Typography>
              <Typography variant="body2">Result: {result}</Typography>
              <Typography variant="body2">Senast start: {lastStart}</Typography>
              <Typography variant="body2">Senast slut: {lastEnd}</Typography>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CacheControlList;